import React, { useState, useEffect } from 'react';
import { getAllCompanies } from '../services/companiesService';
import Pagination from '@mui/material/Pagination';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { useAuth } from '../context/authContext';
import { industries } from '../constants/options';
import SocioDetailModal from '../components/company/socioModalDetail';
import { Banner } from '../components/common/Banner';
import SectionTips from '../components/common/SectionTips';
import { PageLoader } from '../components/loader';
import * as XLSX from 'xlsx';
import ExcelJS from 'exceljs';
import { getContactsListByCompanySafe } from '../services/contactsExportService';

const theme = createTheme({
  palette: {
    primary: { main: '#0488AF' },
    secondary: { main: '#C42251' },
  },
});

const ListadoSocios = () => {
  const [socios, setSocios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedIndustry, setSelectedIndustry] = useState('');
  const [page, setPage] = useState(1);
  const [selectedSocio, setSelectedSocio] = useState(null);
  const itemsPerPage = 9;
  const { user } = useAuth();

  useEffect(() => {
    let isMounted = true;
    const fetchSocios = async () => {
      try {
        const data = await getAllCompanies(user);
        if (isMounted) {
          setSocios(data);
          setLoading(false);
        }
      } catch (err) {
        if (isMounted) {
          setError(err.message);
          setLoading(false);
          console.error('Error al cargar socios:', err);
        }
      }
    };
    fetchSocios();
    return () => { isMounted = false; };
  }, [user]);

  const filteredSocios = socios.filter(socio => {
    if (!socio) return false;

    const role = socio.role?.toLowerCase();
    const businessName = socio.business_name?.toLowerCase() || '';
    const tradeName = socio.trade_name?.toLowerCase() || '';
    const industry = socio.industry?.toLowerCase() || '';

    const searchTermLower = searchTerm.toLowerCase();
    const selectedIndustryLower = selectedIndustry.toLowerCase();

    const nameMatch =
      businessName.includes(searchTermLower) ||
      tradeName.includes(searchTermLower);

    const industryMatch =
      selectedIndustryLower === '' || industry === selectedIndustryLower;

    const isNotAdmin = role !== 'admin';

    return isNotAdmin && nameMatch && industryMatch;
  });

  const pageCount = Math.ceil(filteredSocios.length / itemsPerPage);
  const paginatedSocios = filteredSocios.slice(
    (page - 1) * itemsPerPage,
    page * itemsPerPage
  );

  useEffect(() => {
    setPage(1);
  }, [searchTerm, selectedIndustry]);

  const handleCardClick = (socio) => setSelectedSocio(socio);
  const handleCloseModal = () => setSelectedSocio(null);
  const handleClearFilters = () => { setSearchTerm(''); setSelectedIndustry(''); };

  // =========================================================
  // [CAMBIO] Exportar UNA sola hoja: Empresas + Contactos
  // - Una fila por contacto asociado a la empresa.
  // - Si no hay contactos, se agrega fila con campos de contacto vacíos.
  // =========================================================
  const generateExcelReport = async () => {
    try {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Socios y Contactos');

      // Estilos de cabecera y zebra (manteniendo tu estética)
      const headerStyle = {
        font: { bold: true, size: 12, color: { argb: 'FFFFFFFF' } },
        fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF2F75B5' } },
        border: {
          top: { style: 'medium', color: { argb: 'FF000000' } },
          bottom: { style: 'medium', color: { argb: 'FF000000' } },
          left: { style: 'medium', color: { argb: 'FF000000' } },
          right: { style: 'medium', color: { argb: 'FF000000' } },
        },
        alignment: { vertical: 'middle', horizontal: 'center' },
      };
      const greenRowStyle = {
        fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE2EFDA' } },
      };

      // [CAMBIO] Columnas: datos de EMPRESA + datos de CONTACTO
      worksheet.columns = [
        { header: 'RUT', key: 'rut', width: 15 },
        { header: 'RAZÓN SOCIAL', key: 'business_name', width: 30 },
        { header: 'NOMBRE COMERCIAL', key: 'trade_name', width: 25 },
        { header: 'RUBRO', key: 'industry', width: 20 },
        { header: 'DIRECCIÓN', key: 'address', width: 35 },
        { header: 'REGIÓN', key: 'region', width: 15 },
        { header: 'PROVINCIA', key: 'province', width: 15 },
        { header: 'COMUNA', key: 'district', width: 15 },
        { header: 'EMAIL EMPRESA', key: 'contact_email', width: 25 },
        { header: 'TELÉFONO FIJO', key: 'landline_phone', width: 15 },
        { header: 'TELÉFONO MÓVIL', key: 'mobile_phone', width: 15 },
        { header: 'SITIO WEB', key: 'web_page', width: 20 },
        { header: 'MONTO UF', key: 'amount', width: 12, style: { numFmt: '"$"#,##0.00' } },
        { header: 'PERIODICIDAD', key: 'periodicity', width: 15 },
        { header: 'DESCRIPCIÓN', key: 'description', width: 30 },
        { header: 'ESTADO', key: 'status', width: 12 },
        { header: 'FECHA INGRESO', key: 'entry_date', width: 15 },
        { header: 'TAMAÑO EMPRESA', key: 'company_size', width: 15 },
        // ---- Contacto asociado ----
        { header: 'NOMBRE CONTACTO', key: 'contact_name', width: 26 },
        { header: 'CARGO CONTACTO', key: 'title_job', width: 22 },
        { header: 'ÁREA CONTACTO', key: 'area', width: 18 },
        { header: 'EMAIL CONTACTO', key: 'email', width: 28 },
        { header: 'TELÉFONO CONTACTO', key: 'phone_number', width: 18 },
      ];

      worksheet.getRow(1).eachCell((cell) => { cell.style = headerStyle; });

      // [CAMBIO] Para cada empresa filtrada, traer contactos en paralelo
      const results = await Promise.all(
        filteredSocios.map(async (socio) => {
          try {
            const name = socio?.business_name ?? '';
            if (!name) return { socio, contacts: [] };
            const contacts = await getContactsListByCompanySafe(name); // el servicio ya puede encargarse de codificar
            return { socio, contacts: Array.isArray(contacts) ? contacts : [] };
          } catch (e) {
            console.error('Error obteniendo contactos de', socio?.business_name, e);
            return { socio, contacts: [] };
          }
        })
      );

      // [CAMBIO] Agregar filas: una por contacto; si no hay, fila de contacto vacío
      results.forEach(({ socio, contacts }) => {
        if (contacts.length > 0) {
          contacts.forEach((c) => {
            worksheet.addRow({
              rut: socio?.rut ?? '',
              business_name: socio?.business_name ?? '',
              trade_name: socio?.trade_name ?? '',
              industry: socio?.industry ?? '',
              address: socio?.address ?? '',
              region: socio?.region ?? '',
              province: socio?.province ?? '',
              district: socio?.district ?? '',
              contact_email: socio?.contact_email ?? '',
              landline_phone: socio?.landline_phone ?? '',
              mobile_phone: socio?.mobile_phone ?? '',
              web_page: socio?.web_page ?? '',
              amount: socio?.amount ?? '',
              periodicity: socio?.periodicity ?? '',
              description: socio?.description ?? '',
              status: socio?.status ?? '',
              entry_date: socio?.entry_date ?? '',
              company_size: socio?.company_size ?? '',
              // Contacto
              contact_name: c?.contact_name ?? '',
              title_job: c?.title_job ?? '',
              area: c?.area ?? '',
              email: c?.email ?? '',
              phone_number: c?.phone_number ?? '',
            });
          });
        } else {
          worksheet.addRow({
            rut: socio?.rut ?? '',
            business_name: socio?.business_name ?? '',
            trade_name: socio?.trade_name ?? '',
            industry: socio?.industry ?? '',
            address: socio?.address ?? '',
            region: socio?.region ?? '',
            province: socio?.province ?? '',
            district: socio?.district ?? '',
            contact_email: socio?.contact_email ?? '',
            landline_phone: socio?.landline_phone ?? '',
            mobile_phone: socio?.mobile_phone ?? '',
            web_page: socio?.web_page ?? '',
            amount: socio?.amount ?? '',
            periodicity: socio?.periodicity ?? '',
            description: socio?.description ?? '',
            status: socio?.status ?? '',
            entry_date: socio?.entry_date ?? '',
            company_size: socio?.company_size ?? '',
            // Contacto vacío
            contact_name: '',
            title_job: '',
            area: '',
            email: '',
            phone_number: '',
          });
        }
      });

      // Zebra rows (aplicar después de insertar)
      worksheet.eachRow({ includeEmpty: false }, (row, rowNumber) => {
        if (rowNumber > 1 && rowNumber % 2 === 0) {
          row.eachCell((cell) => { cell.style = { ...cell.style, ...greenRowStyle }; });
        }
      });

      // Autofiltro en toda la cabecera
      worksheet.autoFilter = {
        from: { row: 1, column: 1 },
        to:   { row: 1, column: worksheet.columnCount },
      };

      // Descargar archivo
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Reporte_Socios_y_Contactos_CIDERE_${new Date().toISOString().split('T')[0]}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error al generar el reporte:', error);
      alert('Ocurrió un error al generar el reporte');
    }
  };

  const styles = {
    headerGradient: 'bg-gradient-to-b from-[#09274A] to-[#0488AF]',
    cardStyle: 'bg-gradient-to-br from-[#0488AF]/10 to-[#0488AF]/5 border-l-4 border-[#DC3465] cursor-pointer transition-all hover:scale-[1.02] hover:shadow-lg',
    badge: 'bg-gradient-to-r from-[#DC3465] to-[#DC3465]/80 text-white',
    buttonPrimary: 'bg-gradient-to-r from-[#0488AF] to-[#09274A] text-white',
    buttonSecondary: 'bg-gradient-to-r from-[#DC3465] to-[#C42251] text-white',
    errorContainer: 'bg-gradient-to-r from-red-50 to-[#DC3465]/10 border-l-8 border-[#C42251]',
    excelButton: 'bg-gradient-to-r from-[#0488AF] to-[#09274A] hover:from-[#09274A] hover:to-[#0488AF] text-white font-bold py-3 px-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 flex items-center gap-2',
  };

  if (loading) return <PageLoader message="Cargando socios..." />;

  if (error) {
    return (
      <div className={`${styles.errorContainer} p-6 my-8 max-w-3xl mx-auto rounded-xl shadow-sm`}>
        <div className="flex items-start">
          <div className="flex-shrink-0">
            <svg className="h-8 w-8 text-[#C42251]" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-4">
            <h3 className="text-lg font-bold text-[#C42251]">Error al cargar los datos</h3>
            <p className="mt-2 text-[#C42251]">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className={`mt-4 inline-flex items-center px-4 py-2 ${styles.buttonSecondary} font-medium rounded-lg shadow-md hover:opacity-90 transition-all`}
            >
              Reintentar
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <SocioDetailModal socio={selectedSocio} onClose={handleCloseModal} />

      <div className={`min-h-screen bg-gray-50 px-4 py-8 ${selectedSocio ? 'filter blur-sm' : ''}`}>
        <div className="max-w-7xl mx-auto">
          <Banner title="Listado de Socios" message="Socios de CIDERE" />
          
          {/* Controles superiores */}
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6 mt-4">
            <SectionTips
              section="la sección de socios"
              buttonTitle="¿Cómo usar esta sección?"
              tipsTitle="¿Cómo buscar y filtrar socios?"
              tipsList={[
                'Puedes buscar por nombre.',
                'Utiliza los filtros para encontrar lo que necesitas.',
                'Haz clic en un socio para ver más detalles.',
              ]}
              finalNote="Consejo: Mantén tus datos actualizados."
              color="#1976d2"
            />

            {/* Botón de exportación: visible sólo para admin (igual que tu original) */}
            {user?.role === 'admin' && (
              <div className="w-full md:w-auto flex justify-end">
                <button
                  onClick={generateExcelReport}
                  className={`${styles.excelButton} ${filteredSocios.length === 0 ? 'opacity-50 cursor-not-allowed' : 'hover:scale-105'}`}
                  disabled={filteredSocios.length === 0}
                  title={filteredSocios.length === 0 ? 'No hay datos para exportar' : 'Exportar Socios + Contactos (Excel)'} // [CAMBIO] aclaración en tooltip
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Exportar Socios
                </button>
              </div>
            )}
          </div>

          {/* Filtros */}
          <div className="mb-8 max-w-7xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="search-name" className="block text-sm font-medium text-gray-700 mb-1">
                  Buscar por nombre
                </label>
                <div className="relative">
                  <input
                    id="search-name"
                    type="text"
                    placeholder="🔍 Nombre del socio..."
                    className="w-full px-5 py-3 border-0 rounded-xl focus:ring-4 focus:ring-[#0488AF]/30 focus:border-[#0488AF] shadow-md text-gray-700 font-medium"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    aria-label="Buscar socios por nombre"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="search-category" className="block text-sm font-medium text-gray-700 mb-1">
                  Filtrar por rubro
                </label>
                <select
                  id="search-category"
                  className="w-full px-5 py-3 border-0 rounded-xl focus:ring-4 focus:ring-[#0488AF]/30 focus:border-[#0488AF] shadow-md text-gray-700 font-medium appearance-none bg-white"
                  value={selectedIndustry}
                  onChange={(e) => setSelectedIndustry(e.target.value)}
                  aria-label="Filtrar socios por categoría"
                >
                  <option value="">Todos los rubros</option>
                  {industries.map((industry) => (
                    <option key={industry.value} value={industry.value}>
                      {industry.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {(searchTerm || selectedIndustry) && (
              <div className="mt-4 flex justify-end">
                <button
                  onClick={handleClearFilters}
                  className="px-4 py-2 text-lg font-semibold text-[#0488AF] hover:text-[#C42251] transition-colors"
                >
                  Limpiar filtros
                </button>
              </div>
            )}
          </div>

          {/* Tarjetas */}
          {filteredSocios.length > 0 ? (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mb-8">
                {paginatedSocios.map((socio, index) => (
                  <div
                    key={`${socio.rut}-${index}`}
                    className={`${styles.cardStyle} rounded-xl shadow-md overflow-hidden h-full transition-transform transform hover:scale-[1.02] hover:shadow-lg`}
                    onClick={() => handleCardClick(socio)}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className="p-5 h-full flex flex-col">
                      <div className="flex items-center gap-1 mb-3">
                        {socio.logo ? (
                          <div className="w-20 h-20 rounded-full bg-white flex items-center justify-center overflow-hidden mr-3 flex-shrink-0 shadow-md border-4">
                            <img
                              src={socio.logo}
                              alt={`Logo de ${socio.business_name}`}
                              className="w-full h-full object-contain p-1"
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(socio.business_name || 'S')}&background=0488AF&color=fff&size=80`;
                              }}
                              loading="lazy"
                            />
                          </div>
                        ) : (
                          <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#0488AF] to-[#09274A] flex items-center justify-center text-white text-xl font-bold mr-3 flex-shrink-0 shadow-md">
                            {(socio.trade_name || 'S').charAt(0).toUpperCase()}
                          </div>
                        )}
                        <div className="min-w-0">
                          <h2 className="text-lg font-bold text-gray-800 truncate">{socio.trade_name || 'Nombre no disponible'}</h2>
                          <span className={`inline-block mt-1 px-2 py-1 text-xs font-semibold ${styles.badge} rounded-full`}>
                            {socio.industry || 'Rubro no especificado'}
                          </span>
                        </div>
                      </div>

                      <div className="space-y-2 flex-grow">
                        {socio.contact_email && (
                          <div className="flex items-start">
                            <div className="bg-[#0488AF]/10 p-1 rounded-lg mr-2">
                              <svg className="w-4 h-4 text-[#0488AF]" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                              </svg>
                            </div>
                            <span className="text-sm text-gray-700 font-medium break-all">
                              {socio.contact_email.split('@')[0]}<wbr />@{socio.contact_email.split('@')[1]}
                            </span>
                          </div>
                        )}

                        {socio.landline_phone && (
                          <div className="flex items-start">
                            <div className="bg-[#0488AF]/10 p-1 rounded-lg mr-2">
                              <svg className="w-4 h-4 text-[#0488AF]" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                              </svg>
                            </div>
                            <span className="text-sm text-gray-700 font-medium truncate">
                              {socio.landline_phone}
                            </span>
                          </div>
                        )}

                        {socio.address && (
                          <div className="flex items-start">
                            <div className="bg-[#0488AF]/10 p-1 rounded-lg mr-2">
                              <svg className="w-4 h-4 text-[#0488AF]" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                              </svg>
                            </div>
                            <span className="text-sm text-gray-700 font-medium line-clamp-2">
                              {socio.address}
                            </span>
                          </div>
                        )}
                      </div>

                      {socio.business_name && (
                        <div className="mt-3 pt-3 border-t border-gray-200/50">
                          <p className="text-xs text-gray-600 italic truncate" title={socio.business_name}>
                            {socio.business_name}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {pageCount > 1 && (
                <div className="flex justify-center mt-8">
                  <Pagination
                    count={pageCount}
                    page={page}
                    onChange={(event, value) => setPage(value)}
                    color="primary"
                    size="large"
                    siblingCount={1}
                    className="[&>.MuiPagination-ul>.Mui-selected]:bg-[#0488AF] [&>.MuiPagination-ul>.Mui-selected]:text-white"
                  />
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-12 bg-white rounded-2xl shadow-md border border-gray-200 max-w-3xl mx-auto">
              <svg className="mx-auto h-16 w-16 text-[#DC3465]" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <h3 className="mt-4 text-2xl font-bold text-gray-800">No se encontraron resultados</h3>
              <p className="mt-2 text-gray-600 mb-6">Intenta con otros términos de búsqueda.</p>
              <button
                onClick={handleClearFilters}
                className={`inline-flex items-center px-6 py-3 ${styles.buttonPrimary} font-bold rounded-xl shadow-lg hover:opacity-90 transition-all`}
              >
                Limpiar filtros
              </button>
            </div>
          )}
        </div>
      </div>
    </ThemeProvider>
  );
};

export default ListadoSocios;
